import React, { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
    MdVerified,
    MdContentCopy,
    MdChevronRight,
    MdAccountBalanceWallet,
    MdPayments,
    MdSwapHoriz,
    MdSend,
    MdGroups,
    MdLogout,
    MdGetApp,
} from "react-icons/md";
import { BASE_API_URL, BASE_DOMAIN } from "../helpers/Config";
import axios from "axios";
import toast from "react-hot-toast";

const getStyles = (isDark) => ({
    page: {
        maxWidth: "480px",
        margin: "0 auto",
        minHeight: "100%",
        backgroundColor: isDark ? "#111827" : "#f8fafc",
        color: isDark ? "#f9fafb" : "#111827",
    },
    hero: {
        background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 55%, #5b21b6 100%)",
        borderRadius: "16px",
        boxShadow: isDark
            ? "0 12px 32px rgba(99, 102, 241, 0.28)"
            : "0 12px 28px rgba(99, 102, 241, 0.2)",
    },
    avatarRing: {
        padding: "3px",
        borderRadius: "50%",
        background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.55) 100%)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    },
    onlineDot: {
        width: "14px",
        height: "14px",
        borderRadius: "50%",
        backgroundColor: "#22c55e",
        border: "2px solid #fff",
        position: "absolute",
        bottom: "2px",
        right: "2px",
    },
    userIdPill: {
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: "999px",
        fontSize: "0.72rem",
        fontWeight: 600,
        backgroundColor: "rgba(255,255,255,0.18)",
        color: "#f8fafc",
        border: "1px solid rgba(255,255,255,0.25)",
    },
    vipBadge: {
        fontSize: "0.68rem",
        fontWeight: 600,
        color: "rgba(255,255,255,0.85)",
        letterSpacing: "0.03em",
    },
    balanceTile: (gradient) => ({
        borderRadius: "12px",
        padding: "12px 10px",
        minHeight: "72px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: gradient,
        boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
    }),
    balanceAmount: {
        color: "#ffffff",
        fontWeight: 700,
        fontSize: "1rem",
        lineHeight: 1.2,
        textShadow: "0 1px 3px rgba(0,0,0,0.25)",
    },
    balanceLabel: {
        color: "rgba(255,255,255,0.9)",
        fontSize: "0.68rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        marginTop: "4px",
    },
    referralCard: {
        borderRadius: "14px",
        background: isDark
            ? "linear-gradient(135deg, #0f766e 0%, #047857 50%, #065f46 100%)"
            : "linear-gradient(135deg, #14b8a6 0%, #059669 100%)",
        boxShadow: isDark
            ? "0 8px 24px rgba(16, 185, 129, 0.2)"
            : "0 8px 20px rgba(5, 150, 105, 0.25)",
    },
    referralInputWrap: {
        borderRadius: "10px",
        backgroundColor: "rgba(255,255,255,0.15)",
        border: "1px solid rgba(255,255,255,0.2)",
        padding: "4px 4px 4px 12px",
    },
    copyBtn: {
        borderRadius: "8px",
        fontWeight: 600,
        fontSize: "0.75rem",
        padding: "6px 12px",
        backgroundColor: "#ffffff",
        color: "#047857",
        border: "none",
        whiteSpace: "nowrap",
    },
    card: {
        backgroundColor: isDark ? "#020617" : "#ffffff",
        borderRadius: "14px",
        border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.35)" : "0 4px 16px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
    },
    sectionTitle: {
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: isDark ? "#9ca3af" : "#6b7280",
        marginBottom: 0,
    },
    quickAction: (bg, shadow) => ({
        borderRadius: "12px",
        minHeight: "76px",
        padding: "10px 8px",
        background: bg,
        boxShadow: shadow,
        border: "none",
        color: "#fff",
        textDecoration: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
    }),
    listBorder: isDark ? "#1f2937" : "#f1f5f9",
    hoverBg: isDark ? "rgba(255,255,255,0.04)" : "rgba(99, 102, 241, 0.04)",
    menuIconBg: (isDark, accent) =>
        isDark ? `${accent}33` : `${accent}18`,
    textPrimary: isDark ? "#f9fafb" : "#111827",
    textSecondary: isDark ? "#9ca3af" : "#6b7280",
    modal: {
        backgroundColor: isDark ? "#020617" : "#ffffff",
        color: isDark ? "#f9fafb" : "#111827",
        borderRadius: "16px",
        border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
        boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
    },
    skeleton: {
        backgroundColor: isDark ? "#1f2937" : "#e5e7eb",
        borderRadius: "8px",
    },
});

const OPTION_ACCENTS = {
    primary: "#6366f1",
    info: "#0ea5e9",
    warning: "#f59e0b",
    danger: "#ef4444",
    secondary: "#64748b",
    success: "#22c55e",
    dark: "#475569",
};

const profileOptions = [
    { id: "edit", label: "My Profile", icon: "👤", description: "View your account details", color: "primary", path: "edit-profile" },
    { id: "coin-transaction", label: "All Transaction History", icon: "🪙", description: "View all your transactions of coin", color: "info", path: "transaction-history" },
    { id: "usdt-transaction", label: "USDT Transaction History", icon: "💵", description: "View all your transactions of USDT", color: "info", path: "usdt-transaction-history" },
    { id: "deposit", label: "Deposit History", icon: "⬇️", description: "View all your deposit history of USDT", color: "info", path: "deposit-history" },
    { id: "withdrawl", label: "Withdrawl History", icon: "⬆️", description: "View all your withdrawl history of USDT", color: "info", path: "withdrawl-history" },
    { id: "coupon", label: "Coupons", icon: "🎫", description: "View & claim coupon codes", color: "info", path: "coupon" },
    { id: "claim-commission", label: "Claim Commission", icon: "💰", description: "Claim your earned commission", color: "warning", path: "commission-claim" },
    { id: "subordinate-data", label: "Subordinate Data", icon: "📈", description: "View subordinate data", color: "warning", path: "subordinate-data" },
    { id: "commission", label: "Commission Chart", icon: "📊", description: "View level wise commission chart", color: "primary", path: "commission-chart" },
    { id: "security", label: "Security", icon: "🔐", description: "Change your login password", color: "danger", path: "change-password" },
    { id: "team", label: "Trading Team", icon: "👥", description: "View my trading team", color: "secondary", path: "team" },
    { id: "support", label: "Help & Support", icon: "🎧", description: "24/7 customer support", color: "dark", path: "support" },
    { id: "install-app", label: "Install App", icon: "📱", description: "Install app on your device", color: "success", path: null, isInstall: true },
    { id: "logout", label: "Logout", icon: "🚪", description: "Sign out from your account", color: "danger", path: null, isLogout: true },
];

const QUICK_ACTIONS = [
    {
        to: "../fund-deposit",
        label: "Deposit",
        sub: "Deposit USDT",
        icon: MdAccountBalanceWallet,
        bg: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
        shadow: "0 4px 14px rgba(22, 163, 74, 0.35)",
    },
    {
        to: "../withdrawl",
        label: "Withdraw",
        sub: "Withdraw USDT",
        icon: MdPayments,
        bg: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
        shadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
    },
    {
        to: "../usdt-swap",
        label: "Swap",
        sub: "Coin ⇄ USDT",
        icon: MdSwapHoriz,
        bg: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
        shadow: "0 4px 14px rgba(245, 158, 11, 0.35)",
        subColor: "#1e293b",
    },
    {
        to: "../p2p-transfer",
        label: "P2P Transfer",
        sub: "Transfer USDT",
        icon: MdSend,
        bg: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
        shadow: "0 4px 14px rgba(14, 165, 233, 0.35)",
    },
];

const ProfileMenuRow = ({ option, styles, isDark, onClick, trailing, isLast }) => {
    const accent = OPTION_ACCENTS[option.color] || OPTION_ACCENTS.primary;
    const content = (
        <motion.div
            className="px-3 py-3"
            style={{
                borderBottom: isLast ? "none" : `1px solid ${styles.listBorder}`,
                cursor: onClick ? "pointer" : "default",
            }}
            whileHover={{ backgroundColor: styles.hoverBg }}
            transition={{ duration: 0.12 }}
            onClick={onClick}
        >
            <div className="d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center min-w-0">
                    <span
                        className="me-3 flex-shrink-0 d-flex align-items-center justify-content-center"
                        style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "12px",
                            fontSize: "1.15rem",
                            backgroundColor: styles.menuIconBg(isDark, accent),
                        }}
                    >
                        {option.icon}
                    </span>
                    <div className="min-w-0">
                        <div
                            className="fw-semibold text-truncate"
                            style={{ fontSize: "0.875rem", color: styles.textPrimary }}
                        >
                            {option.label}
                        </div>
                        <small
                            className="d-block text-truncate"
                            style={{ fontSize: "0.75rem", color: styles.textSecondary }}
                        >
                            {option.description}
                        </small>
                    </div>
                </div>
                {trailing || (
                    <MdChevronRight size={22} style={{ color: styles.textSecondary, flexShrink: 0 }} />
                )}
            </div>
        </motion.div>
    );

    return content;
};

const ProfileScreen = ({ theme = "light" }) => {
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [UserProfile, setUserProfile] = useState({
        name: "",
        user_id: "",
        balance: 0,
        usdt: 0,
        code: "",
    });

    const [isRunningInPWA, setIsRunningInPWA] = useState(false);
    const [hasNativePrompt, setHasNativePrompt] = useState(false);

    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getStyles(isDark), [isDark]);

    const detectStandalone = useCallback(() => {
        const isStandaloneDisplayMode =
            window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
        const isIOSStandalone = window.navigator.standalone === true;
        return Boolean(isStandaloneDisplayMode || isIOSStandalone);
    }, []);

    useEffect(() => {
        const update = () => {
            const running = detectStandalone();
            setIsRunningInPWA(running);
            setHasNativePrompt(Boolean(window.__TB_PWA_PROMPT__));
        };

        update();

        const onAvailable = () => update();
        const onInstalled = () => update();

        window.addEventListener("tb-pwa-prompt-available", onAvailable);
        window.addEventListener("tb-pwa-installed", onInstalled);

        const mm = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;
        const onMMChange = () => update();
        if (mm && mm.addEventListener) mm.addEventListener("change", onMMChange);
        else if (mm && mm.addListener) mm.addListener(onMMChange);

        return () => {
            window.removeEventListener("tb-pwa-prompt-available", onAvailable);
            window.removeEventListener("tb-pwa-installed", onInstalled);
            if (mm && mm.removeEventListener) mm.removeEventListener("change", onMMChange);
            else if (mm && mm.removeListener) mm.removeListener(onMMChange);
        };
    }, [detectStandalone]);

    const handleInstallClick = async () => {
        if (isRunningInPWA) return;

        const promptEvent = window.__TB_PWA_PROMPT__;
        if (!promptEvent) {
            toast.error("Install prompt not available. PWA is not installable yet.");
            return;
        }

        try {
            promptEvent.prompt();
            const choice = await promptEvent.userChoice;

            if (choice?.outcome === "accepted") {
                toast.success("Installation started!");
            } else {
                toast.error("Installation cancelled.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to open install prompt.");
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    };

    const handleLogout = () => setShowLogoutModal(true);
    const confirmLogout = () => {
        setShowLogoutModal(false);
        localStorage.removeItem("a5srf4wer7w1cd");
        navigate("/");
    };
    const cancelLogout = () => setShowLogoutModal(false);

    const copyReferralLink = () => {
        const refer_link = `${BASE_DOMAIN}/register/${UserProfile?.code}`;
        navigator.clipboard
            .writeText(refer_link)
            .then(() => toast.success("Referral link copied to clipboard!"))
            .catch(() => {
                const textArea = document.createElement("textarea");
                textArea.value = refer_link;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
                toast.success("Referral link copied to clipboard!");
            });
    };

    const FetchProfile = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = response?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
                return;
            }

            if (data?.data) setUserProfile({ ...data.data });
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        FetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredProfileOptions = useMemo(() => {
        return profileOptions.filter((opt) => {
            if (opt.id !== "install-app") return true;
            if (isRunningInPWA) return false;
            return hasNativePrompt;
        });
    }, [isRunningInPWA, hasNativePrompt]);

    const referralLink = `${BASE_DOMAIN}/register/${UserProfile?.code || ""}`;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="h-100 overflow-auto px-3 py-3"
            style={styles.page}
        >
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
                {/* Hero */}
                <motion.div variants={itemVariants} className="mb-3 p-4" style={styles.hero}>
                    <div className="text-center">
                        <div className="position-relative d-inline-block mb-3">
                            <div style={styles.avatarRing}>
                                <img
                                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=120&q=80"
                                    alt="Profile"
                                    className="rounded-circle object-fit-cover"
                                    style={{ width: "84px", height: "84px", display: "block" }}
                                />
                            </div>
                            <span style={styles.onlineDot} title="Online" />
                        </div>

                        {loading ? (
                            <>
                                <div
                                    className="mx-auto mb-2"
                                    style={{ ...styles.skeleton, width: "140px", height: "20px", opacity: 0.5 }}
                                />
                                <div
                                    className="mx-auto"
                                    style={{ ...styles.skeleton, width: "100px", height: "14px", opacity: 0.4 }}
                                />
                            </>
                        ) : (
                            <>
                                <div className="d-flex align-items-center justify-content-center mb-1">
                                    <h1 className="h5 fw-bold mb-0 me-1 text-white">{UserProfile?.name || "—"}</h1>
                                    <span className="text-white" title="Verified">
                                        <MdVerified size={20} />
                                    </span>
                                </div>
                                {UserProfile?.user_id && (
                                    <div className="mb-1">
                                        <span style={styles.userIdPill}>{UserProfile.user_id}</span>
                                    </div>
                                )}
                                <p className="mb-0" style={styles.vipBadge}>
                                    Professional Trader · VIP Member
                                </p>
                            </>
                        )}
                    </div>

                    <div className="row g-2 mt-3">
                        <div className="col-6">
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                style={styles.balanceTile(
                                    "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%)"
                                )}
                            >
                                {loading ? (
                                    <div style={{ ...styles.skeleton, width: "60%", height: "18px", opacity: 0.4 }} />
                                ) : (
                                    <span style={styles.balanceAmount}>{UserProfile?.balance ?? 0}</span>
                                )}
                                <span style={styles.balanceLabel}>Total Balance</span>
                            </motion.div>
                        </div>
                        <div className="col-6">
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                style={styles.balanceTile(
                                    "linear-gradient(135deg, rgba(16,185,129,0.45) 0%, rgba(5,150,105,0.35) 100%)"
                                )}
                            >
                                {loading ? (
                                    <div style={{ ...styles.skeleton, width: "60%", height: "18px", opacity: 0.4 }} />
                                ) : (
                                    <span style={styles.balanceAmount}>{UserProfile?.usdt ?? 0}</span>
                                )}
                                <span style={styles.balanceLabel}>USDT Balance</span>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {/* Referral */}
                <motion.div variants={itemVariants} className="mb-3 p-3" style={styles.referralCard}>
                    <div className="d-flex align-items-center mb-3">
                        <div
                            className="me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "10px",
                                backgroundColor: "rgba(255,255,255,0.2)",
                            }}
                        >
                            <MdGroups size={22} className="text-white" />
                        </div>
                        <div>
                            <h6 className="fw-bold mb-0 text-white" style={{ fontSize: "0.95rem" }}>
                                Refer & Earn
                            </h6>
                            <small style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem" }}>
                                Invite friends and earn rewards
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2" style={styles.referralInputWrap}>
                        <input
                            type="text"
                            value={referralLink}
                            readOnly
                            className="form-control border-0 bg-transparent text-white flex-grow-1"
                            style={{ fontSize: "0.72rem", minWidth: 0 }}
                            onClick={(e) => e.target.select()}
                        />
                        <motion.button
                            type="button"
                            className="btn btn-sm d-flex align-items-center gap-1"
                            style={styles.copyBtn}
                            onClick={copyReferralLink}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            disabled={loading || !UserProfile?.code}
                        >
                            <MdContentCopy size={14} />
                            Copy
                        </motion.button>
                    </div>
                </motion.div>

                {/* Quick actions */}
                <motion.div variants={itemVariants} className="mb-3">
                    <p className="mb-2 px-1" style={styles.sectionTitle}>
                        Quick actions
                    </p>
                    <div className="row g-2">
                        {QUICK_ACTIONS.map((action) => {
                            const Icon = action.icon;
                            return (
                                <div key={action.to} className="col-6">
                                    <Link to={action.to} style={{ textDecoration: "none" }}>
                                        <motion.div
                                            whileHover={{ scale: 1.03, y: -2 }}
                                            whileTap={{ scale: 0.98 }}
                                            style={styles.quickAction(action.bg, action.shadow)}
                                        >
                                            <Icon size={22} className="mb-1" />
                                            <span className="fw-bold" style={{ fontSize: "0.8rem" }}>
                                                {action.label}
                                            </span>
                                            <span
                                                style={{
                                                    fontSize: "0.65rem",
                                                    opacity: 0.9,
                                                    color: action.subColor || "rgba(255,255,255,0.85)",
                                                }}
                                            >
                                                {action.sub}
                                            </span>
                                        </motion.div>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Account menu */}
                <motion.div variants={itemVariants} style={styles.card}>
                    <div
                        className="px-3 py-3"
                        style={{
                            borderBottom: `1px solid ${styles.listBorder}`,
                            backgroundColor: isDark ? "rgba(15, 23, 42, 0.5)" : "#f8fafc",
                        }}
                    >
                        <p style={styles.sectionTitle}>Account management</p>
                    </div>

                    <div>
                        {filteredProfileOptions.map((option, index) => {
                            const isLast = index === filteredProfileOptions.length - 1;

                            if (option.isLogout) {
                                return (
                                    <ProfileMenuRow
                                        key={option.id}
                                        option={option}
                                        styles={styles}
                                        isDark={isDark}
                                        isLast={isLast}
                                        onClick={handleLogout}
                                        trailing={
                                            <MdLogout size={20} style={{ color: "#ef4444", flexShrink: 0 }} />
                                        }
                                    />
                                );
                            }

                            if (option.isInstall) {
                                return (
                                    <ProfileMenuRow
                                        key={option.id}
                                        option={option}
                                        styles={styles}
                                        isDark={isDark}
                                        isLast={isLast}
                                        onClick={handleInstallClick}
                                        trailing={
                                            <MdGetApp size={20} style={{ color: "#22c55e", flexShrink: 0 }} />
                                        }
                                    />
                                );
                            }

                            return (
                                <Link
                                    key={option.id}
                                    to={"../" + (option?.path || "")}
                                    style={{ textDecoration: "none", color: "inherit" }}
                                >
                                    <ProfileMenuRow
                                        option={option}
                                        styles={styles}
                                        isDark={isDark}
                                        isLast={isLast}
                                    />
                                </Link>
                            );
                        })}
                    </div>
                </motion.div>
            </motion.div>

            {/* Logout modal */}
            {showLogoutModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                    tabIndex="-1"
                    onClick={cancelLogout}
                >
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0" style={styles.modal}>
                            <div className="modal-body p-4 text-center">
                                <div
                                    className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "52px",
                                        height: "52px",
                                        borderRadius: "14px",
                                        backgroundColor: isDark ? "rgba(239,68,68,0.15)" : "#fef2f2",
                                    }}
                                >
                                    <MdLogout size={28} style={{ color: "#ef4444" }} />
                                </div>
                                <h5 className="fw-bold mb-2" style={{ color: styles.textPrimary }}>
                                    Confirm logout
                                </h5>
                                <p className="mb-0" style={{ fontSize: "0.9rem", color: styles.textSecondary }}>
                                    Are you sure you want to sign out of your account?
                                </p>
                            </div>
                            <div className="modal-footer border-0 pt-0 pb-4 px-4 gap-2 d-flex">
                                <button
                                    type="button"
                                    className="btn flex-grow-1"
                                    style={{
                                        borderRadius: "10px",
                                        fontWeight: 600,
                                        border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                                        backgroundColor: isDark ? "#111827" : "#f8fafc",
                                        color: styles.textPrimary,
                                    }}
                                    onClick={cancelLogout}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger flex-grow-1"
                                    style={{ borderRadius: "10px", fontWeight: 600 }}
                                    onClick={confirmLogout}
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default ProfileScreen;
